/**
 * Widget Vue d'ensemble Propriétaire
 * Phase 3.2 - Composant optimisé et réutilisable
 */

import React from 'react';
import {
  Box,
  Typography,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Event as EventIcon,
  Home as HomeIcon,
  Star as StarIcon
} from '@mui/icons-material';

import { DashboardWidget, StatusIndicator, TrendIndicator } from '../DashboardBase';
import { formatCurrency, formatPercentage, PERIOD_LABELS } from '../../../constants/dashboardConstants';

const OwnerOverviewWidget = ({ data, loading, period, onRefresh }) => {
  console.log('🔥 WIDGET - DATA REÇUE:', data, 'loading:', loading);
  
  if (!data && !loading) {
    console.log('🔥 WIDGET - AFFICHAGE "AUCUNE DONNÉE" car data=', data, 'loading=', loading);
    return (
      <DashboardWidget
        title="Vue d'ensemble"
        loading={loading}
        onRefresh={onRefresh}
      >
        <Typography color="text.secondary">
          Aucune donnée disponible
        </Typography>
      </DashboardWidget>
    );
  }

  const {
    total_revenue = 0,
    pending_revenue = 0,
    active_reservations = 0,
    total_listings = 0,
    occupancy_rate = 0,
    average_rating = 0,
    trends = {}
  } = data || {};

  const highlights = [
    {
      label: 'Revenus réalisés',
      value: formatCurrency(total_revenue),
      trend: trends.revenue?.value, // Extraire la valeur numérique
      icon: <TrendingUpIcon />,
      color: 'success'
    },
    {
      label: 'Réservations actives',
      value: active_reservations,
      trend: trends.reservations?.value, // Extraire la valeur numérique
      icon: <EventIcon />,
      color: 'primary'
    },
    {
      label: 'Annonces actives',
      value: total_listings,
      icon: <HomeIcon />,
      color: 'info'
    },
    {
      label: 'Note moyenne',
      value: `${average_rating}/5`,
      icon: <StarIcon />,
      color: 'warning'
    }
  ];

  return (
    <DashboardWidget
      title={`Vue d'ensemble - ${PERIOD_LABELS[period]}`}
      loading={loading}
      onRefresh={onRefresh}
      height={400}
    >
      <Grid container spacing={3}>
        {/* Métriques principales */}
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom>
            Indicateurs clés
          </Typography>
          
          <List>
            {highlights.map((item, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemIcon sx={{ color: `${item.color}.main` }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="body1">
                          {item.label}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="h6" color={`${item.color}.main`}>
                            {item.value}
                          </Typography>
                          {item.trend !== undefined && (
                            <TrendIndicator value={item.trend} />
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < highlights.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Grid>

        {/* Taux d'occupation et revenus en attente */}
        <Grid item xs={12} md={4}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Performance
            </Typography>
            
            {/* Taux d'occupation */}
            <Box mb={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Taux d'occupation
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatPercentage(occupancy_rate)}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={occupancy_rate} 
                sx={{ height: 8, borderRadius: 4 }}
                color={occupancy_rate > 70 ? 'success' : occupancy_rate > 40 ? 'warning' : 'error'}
              />
            </Box>

            {/* Revenus en attente */}
            {pending_revenue > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Revenus en attente
                </Typography>
                <Typography variant="h5" color="warning.main">
                  {formatCurrency(pending_revenue)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  En cours de traitement
                </Typography>
              </Box>
            )}

            {/* Statut général */}
            <Box mt={3}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Statut général
              </Typography>
              <StatusIndicator
                status={occupancy_rate > 70 ? 'success' : occupancy_rate > 40 ? 'warning' : 'error'}
                label={
                  occupancy_rate > 70 
                    ? 'Excellent'
                    : occupancy_rate > 40 
                    ? 'Bon' 
                    : 'À améliorer'
                }
                variant="chip"
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </DashboardWidget>
  );
};

export default OwnerOverviewWidget;
